import { BRAND_GRADIENT, gradientTextStyle } from '@shared/styles/brand';
import type { Cadence, Plan } from '../api';
import { CheckCircle } from './CheckCircle';
import { featureBacktesting } from '@config/env';

/**
 * Matches a server feature bullet that states the monthly AI-credit allowance, so it can be dropped
 * in favour of the authoritative `aiCreditsPerMonth`. Deliberately loose on the number and on the
 * "AI"/"AI analyst" wording — the point is to catch whatever phrasing the plan config carries,
 * because a duplicate that slips through is exactly the contradiction this exists to prevent.
 */
const CREDITS_FEATURE = /\d+\s+AI[\w\s]*credits?\s+per\s+month/i;

/*
 * ALL THREE CARDS ARE THE SAME SIZE (2026-08-16 user decision, a deliberate override of the Figma
 * frame). The frame sizes them 411 / 395 / 411 and makes the featured card 24px taller so it stands
 * proud of its neighbours; the user asked for three equal cards instead, so the featured card is now
 * distinguished ONLY by its gradient CTA. Do not "restore" the frame's numbers on the strength of
 * reading the design file — the divergence is intentional and was asked for twice.
 */

/**
 * Cents in, display price out: `3000 → "$30"`, `2999 → "$29.99"`, `0 → "$0"`.
 *
 * The old version pinned `maximumFractionDigits: 0` on the assumption that plan prices are always
 * whole dollars. They are not — Pro is **$29.99** — and that rounding meant the card rendered "$30"
 * no matter what the catalog said, silently overstating the price by a cent and making the intended
 * number impossible to display (2026-08-16 user report). Decimals now show only when there are any,
 * so a genuinely whole-dollar plan still reads "$30" rather than "$30.00".
 *
 * $0 deliberately stays "$0" rather than becoming "Free": the free plan is already NAMED Free, and
 * rendering the word twice on one card reads as a mistake.
 */
function formatPrice(cents: number, currency: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}

export interface PlanCardCta {
  label: string;
  disabled: boolean;
  onClick?: () => void;
  /** When set the CTA renders as a link instead of a button — the Custom card's `mailto:`. */
  href?: string;
  /**
   * One line of plain-language status under the button — "Renews 12 Mar 2026", "Pro ends 12 Mar
   * 2026", "Payment failed — Pro is paused". This is what makes a scheduled downgrade LEGIBLE: a
   * disabled "Current plan" on Pro and a disabled "Starts …" on Free say nothing about WHEN on their
   * own, and "when" is the entire question a user asks after clicking cancel.
   */
  note?: string;
}

interface PlanCardProps {
  plan: Plan;
  cadence: Cadence;
  cta: PlanCardCta;
  /** True for the plan the caller is currently on — draws the ring + "Your plan" badge. */
  isCurrent?: boolean;
  /**
   * A neutral chip in the badge slot for a card that is NOT the current plan but is going to be —
   * today only "Scheduled" on the Free card during a pending downgrade. Ignored when `isCurrent`,
   * because "Your plan" and "Scheduled" would be contradictory claims about one card.
   */
  chip?: string;
  /** Replaces the formatted price entirely — the Custom card's "Let's talk". Suppresses "/monthly",
   *  which would be nonsense beside it. */
  priceLabel?: string;
}

/**
 * One API-driven pricing card, measured against the 2026-08-16 Figma frame.
 *
 * **Sizing — all three equal**, by explicit user decision overriding the frame (see the note above
 * the component). Every card is `flex-1` with a zero basis, and the row stretches them to a common
 * height, so width and height match across the row whatever each plan's feature count. The featured
 * plan reads as featured through its gradient CTA alone.
 *
 * The shadow is `filter: drop-shadow`, not `box-shadow`, for the same reason the frame uses it: a
 * card is TWO panels with their own corner radii, and drop-shadow follows the painted silhouette
 * while box-shadow traces the bounding box. The feature panel keeps `flex-1` (the frame's Bot Card
 * is `flex-grow: 1`): it is what absorbs the stretch so the white surface fills the whole card and
 * the shadow has no empty space to trace around.
 *
 * Figma metrics used verbatim: top panel `36/33/32`, bottom panel `32/33/36`, 42px between name,
 * price and CTA, 16px between features, a 32px-high CTA, `#ECF1FC` hairline borders.
 */
export function PlanCard({ plan, cadence, cta, isCurrent = false, chip, priceLabel }: PlanCardProps) {
  // The number is always a monthly figure — annual cadence shows the annual price's per-month
  // equivalent, never the lump annual total (Figma: "/monthly" suffix regardless of cadence).
  const annual = cadence === 'annual';
  const displayCents = annual ? Math.round(plan.annualPriceCents / 12) : plan.monthlyPriceCents;
  const featured = plan.isFeatured;
  // On the annual cadence the headline number is a DERIVED per-month figure, not what anyone is
  // charged — so it is labelled "monthly average" and the real amount is stated underneath. Showing
  // a monthly-looking price for a single yearly charge is the kind of thing that reads as a pricing
  // surprise at checkout (2026-08-16 user request).
  const annualNote = annual && plan.annualPriceCents > 0
    ? `${formatPrice(plan.annualPriceCents, plan.currency)} billed yearly, renews annually`
    : null;

  return (
    <div
      className="relative flex flex-1 flex-col"
      style={{
        // `basis: 0` (via flex-1) so every card takes an equal share regardless of how much text it
        // holds — with `auto` the five-feature Pro card would render wider than its neighbours.
        filter: 'drop-shadow(0px 6px 24px rgba(0, 0, 0, 0.1))',
      }}
    >
      {/* `isCurrent` marks the plan the caller is actually ON (2026-08-16 user request: "mark
          selected on the existing package of the user"). The CTA below already says "Current plan",
          but that is a disabled button, which reads as unavailable rather than as yours. */}
      {isCurrent ? (
        <span
          className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white"
          style={{ background: BRAND_GRADIENT }}
        >
          Your plan
        </span>
      ) : chip !== undefined && (
        /* Same slot, deliberately quieter chrome: this plan is coming, it is not yours yet. The
           gradient is reserved for the plan the caller actually holds. */
        <span
          className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
          style={{ background: 'var(--chip-strong)', color: 'var(--text-muted)' }}
        >
          {chip}
        </span>
      )}

      <div
        className="flex flex-col rounded-t-[32px] bg-canvas-raised"
        style={{ padding: '36px 33px 32px', border: '1px solid var(--canvas-divider)', borderBottom: 'none' }}
      >
        {/* 42px between name / price / CTA — the frame's "Top content" gap. */}
        <div className="flex flex-col" style={{ gap: 42 }}>
          <p className="text-[18px] font-bold leading-8 tracking-[0.75px] text-text">{plan.name}</p>

          <div className="flex items-end gap-2">
            <span className="text-[40px] font-semibold leading-[115%] text-text">
              {priceLabel ?? formatPrice(displayCents, plan.currency)}
            </span>
            {priceLabel === undefined && (
              <span className="text-base font-normal leading-[19px]" style={{ color: 'var(--text-muted)' }}>
                {annual ? 'monthly average' : '/monthly'}
              </span>
            )}
          </div>

          {annualNote !== null && (
            <p className="-mt-8 text-[13px] font-normal leading-5" style={{ color: 'var(--text-muted)' }}>
              {annualNote}
            </p>
          )}

          {/* Accessible name includes the plan name (I6) — several cards can share the same CTA
              label ("Coming soon" on two unpurchasable cards), so a screen-reader user tabbing
              through the row needs "Coming soon — Free" vs "Coming soon — Pro" to tell them apart. */}
          {(() => {
            const ctaClass = `flex h-8 w-full items-center justify-center rounded-lg text-[13px] font-semibold leading-4 transition-opacity disabled:cursor-not-allowed disabled:opacity-50 ${
              featured ? 'text-white' : ''
            }`;
            const ctaStyle = featured
              ? { background: BRAND_GRADIENT }
              : { background: 'var(--canvas-divider)' };
            const body = featured ? cta.label : <span style={gradientTextStyle}>{cta.label}</span>;
            // A link, not a disabled-able button, when the action leaves the app (the Custom card's
            // mailto). Same chrome either way — the shell must not fork just because the CTA does.
            const control = cta.href !== undefined ? (
              <a href={cta.href} aria-label={`${cta.label} (${plan.name})`} className={ctaClass} style={ctaStyle}>
                {body}
              </a>
            ) : (
              <button
                type="button"
                disabled={cta.disabled}
                onClick={cta.onClick}
                aria-label={`${cta.label} (${plan.name})`}
                className={ctaClass}
                style={ctaStyle}
              >
                {body}
              </button>
            );
            if (cta.note === undefined) return control;
            // Own wrapper so the note sits 8px under its button rather than 42px down the card's
            // name/price/CTA rhythm — it belongs to the CTA, not to the next thing in the stack.
            return (
              <div className="flex flex-col gap-2">
                {control}
                <p className="text-center text-[13px] leading-5" style={{ color: 'var(--text-muted)' }}>
                  {cta.note}
                </p>
              </div>
            );
          })()}
        </div>
      </div>

      <div
        className="flex flex-1 flex-col rounded-b-[32px] bg-canvas-raised"
        style={{ padding: '32px 33px 36px', border: '1px solid var(--canvas-divider)', borderTop: 'none' }}
      >
        {/* ONE credits line, from ONE source (2026-08-15). The card used to render a credits bullet
            out of `plan.features` AND a separate `aiCreditsPerMonth` paragraph below it, so the Pro
            card contradicted itself — "250 AI credits per month" over "300 AI credits per month".
            Any credits bullet in the server's feature list is now dropped, and the count is drawn
            solely from `aiCreditsPerMonth`, rendered as a bullet like the rest. */}
        <ul className="flex flex-col" style={{ gap: 16 }}>
          {plan.features.filter(f => !CREDITS_FEATURE.test(f)).map(feature => (
            <li key={feature} className="flex items-center gap-2">
              <CheckCircle />
              <span className="text-base font-normal leading-[19px]" style={{ color: 'var(--text-muted)' }}>
                {feature}
              </span>
            </li>
          ))}
          {/* Follows the Backtesting AI flag (2026-08-17): with the feature off, an allowance of AI
              credits is a promise the product cannot keep. NOTE this hides only the structured
              `aiCreditsPerMonth` field — a free-text "AI backtesting" bullet typed into a plan's
              `features` in the backoffice is server-owned copy and must be edited there. */}
          {featureBacktesting && plan.aiCreditsPerMonth > 0 && (
            <li className="flex items-center gap-2">
              <CheckCircle />
              <span className="text-base font-normal leading-[19px]" style={{ color: 'var(--text-muted)' }}>
                {plan.aiCreditsPerMonth} AI credits per month
              </span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
